import neoApi from './neo.api'

// Square Authentication & Integration Types
interface SquareIntegration {
  id: string
  merchant_id: string
  merchant_name: string
  environment: 'sandbox' | 'production'
  created_at: string
  location_count: number
}

interface SquareLocationAddress {
  addressLine1?: string
  addressLine2?: string
  locality?: string
  administrativeDistrictLevel1?: string
  postalCode?: string
  country?: string
}

// This matches the actual API response from Neo
interface NeoSquareLocation {
  id: string
  integration_square_id: string
  square_location_id: string
  location_name: string
  address?: SquareLocationAddress | null
  timezone?: string | null | undefined
  capabilities?: string[] | null
  status?: string | null | undefined
  webhook_subscription_id?: string | null | undefined
  webhook_signature_key?: string | null | undefined
  webhook_active: boolean
  serial?: string | null | undefined // null when not mapped to a venue
  created_at: string
  updated_at: string
}

interface SquareLocationsResponse {
  locations: NeoSquareLocation[]
}

/** A venue that loses its Square link when an account is disconnected. */
export interface SquareAffectedVenue {
  location_mapping_id: string
  square_location_id: string
  location_name: string
  serial: string
}

/**
 * What neo will actually delete if this account is disconnected.
 *
 * Fetched when the confirmation dialog opens so the warning can name real
 * numbers. The copy this replaced said a disconnect "unlinks all of its
 * locations from your Stampede venues", which the server did not do.
 */
export interface SquareDisconnectPreview {
  integration_id: string
  merchant_name: string
  merchant_id: string
  environment: string
  venues: SquareAffectedVenue[]
  unmapped_locations: number
  records: {
    table_mappings: number
    catalog_items: number
    sold_out_overrides: number
    orders: number
    payments: number
    refunds: number
    customers: number
    gift_cards: number
    invoices: number
    terminal_checkouts: number
    devices: number
    team_members: number
    reservations: number
    sync_jobs: number
  }
}

/** What unmapping one venue removes, and what it leaves alone. */
export interface SquareUnmapPreview {
  integration_id: string
  merchant_name: string
  location_mapping_id: string
  square_location_id: string
  location_name: string
  serial: string | null
  removes: {
    table_mappings: number
    sold_out_overrides: number
    reservation_integrations: number
  }
  keeps: {
    orders: number
    payments: number
  }
}

// Neo Square Authentication API
export const neoSquareAuthApi = neoApi
  .enhanceEndpoints({
    addTagTypes: ['SquareAuth', 'SquareIntegrations', 'SquareLocations'],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      // Authentication
      authSquare: build.mutation<
        { url: string; state: string },
        {
          organizationId: string
          redirect_uri: string
          environment?: 'sandbox' | 'production'
        }
      >({
        query: ({ organizationId, redirect_uri, environment }) => ({
          method: 'POST',
          url: `/organizations/${organizationId}/square/auth`,
          data: {
            redirect_uri,
            environment: environment || 'sandbox',
          },
        }),
        invalidatesTags: ['SquareIntegrations'],
      }),

      // Integration Management
      listSquareIntegrations: build.query<
        { integrations: SquareIntegration[] },
        { organizationId: string }
      >({
        query: ({ organizationId }) => ({
          method: 'GET',
          url: `/organizations/${organizationId}/square/integrations`,
        }),
        providesTags: ['SquareIntegrations'],
      }),

      getSquareIntegration: build.query<
        { integration: SquareIntegration },
        { integrationId: string }
      >({
        query: ({ integrationId }) => ({
          method: 'GET',
          url: `/square/integrations/${integrationId}`,
        }),
        providesTags: (result, error, arg) => [
          { type: 'SquareIntegrations', id: arg.integrationId },
        ],
      }),

      getSquareDisconnectPreview: build.query<
        SquareDisconnectPreview,
        { integrationId: string }
      >({
        query: ({ integrationId }) => ({
          method: 'GET',
          url: `/square/integrations/${integrationId}/disconnect-preview`,
        }),
        providesTags: (result, error, arg) => [
          { type: 'SquareIntegrations', id: arg.integrationId },
        ],
      }),

      disconnectSquare: build.mutation<
        {
          success: boolean
          message: string
          integrationId: string
          revokedAt: string
        },
        { integrationId: string }
      >({
        query: ({ integrationId }) => ({
          method: 'DELETE',
          url: `/square/integrations/${integrationId}`,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'SquareIntegrations', id: arg.integrationId },
          'SquareIntegrations',
          'SquareLocations',
        ],
      }),

      // Location Management
      getSquareLocations: build.query<
        SquareLocationsResponse,
        { integrationId: string }
      >({
        query: ({ integrationId }) => ({
          method: 'GET',
          url: `/square/integrations/${integrationId}/locations`,
        }),
        providesTags: ['SquareLocations'],
      }),

      mapSquareLocation: build.mutation<
        { mapping: any }, // Response contains mapping details from the API
        {
          integrationId: string
          locationId: string // This is the Square location ID (square_location_id)
          locationName: string // The location display name
          serial: string // The venue serial to map to
        }
      >({
        query: ({ integrationId, locationId, locationName, serial }) => ({
          method: 'POST',
          url: `/square/integrations/${integrationId}/locations/map`,
          data: {
            location_id: locationId,
            location_name: locationName,
            serial,
          },
        }),
        invalidatesTags: ['SquareLocations', 'SquareIntegrations'],
      }),

      getSquareUnmapPreview: build.query<
        SquareUnmapPreview,
        { integrationId: string; locationMappingId: string }
      >({
        query: ({ integrationId, locationMappingId }) => ({
          method: 'GET',
          url: `/square/integrations/${integrationId}/locations/${locationMappingId}/unmap-preview`,
        }),
        providesTags: ['SquareLocations'],
      }),

      unmapSquareLocation: build.mutation<
        { success: boolean },
        {
          integrationId: string
          locationId: string
        }
      >({
        query: ({ integrationId, locationId }) => ({
          method: 'DELETE',
          url: `/square/integrations/${integrationId}/locations/${locationId}/map`,
        }),
        invalidatesTags: ['SquareLocations', 'SquareIntegrations'],
      }),

      // Sync Management
      triggerSquareSync: build.mutation<
        { message: string },
        {
          integrationId: string
          resource:
            | 'catalog'
            | 'inventory'
            | 'customers'
            | 'orders'
            | 'locations'
        }
      >({
        query: ({ integrationId, resource }) => ({
          method: 'POST',
          url: `/square/integrations/${integrationId}/sync`,
          data: { resource },
        }),
        invalidatesTags: (result, error, arg) => {
          const tags: any[] = [
            { type: 'SquareIntegrations', id: arg.integrationId },
          ]
          if (arg.resource === 'catalog') tags.push('NeoSquareCatalog')
          if (arg.resource === 'orders') tags.push('NeoSquareOrders')
          if (arg.resource === 'customers') tags.push('NeoSquareCustomers')
          return tags
        },
      }),

      clearSquareCache: build.mutation<
        { message: string },
        {
          integrationId: string
          types?: string
          locationIds?: string
          older_than?: string
        }
      >({
        query: ({ integrationId, ...params }) => ({
          method: 'DELETE',
          url: `/square/integrations/${integrationId}/cache`,
          params,
        }),
        invalidatesTags: ['SquareIntegrations'],
      }),
    }),
  })

// Export hooks
export const {
  useAuthSquareMutation,
  useListSquareIntegrationsQuery,
  useGetSquareIntegrationQuery,
  useDisconnectSquareMutation,
  useGetSquareDisconnectPreviewQuery,
  useGetSquareUnmapPreviewQuery,
  useGetSquareLocationsQuery,
  useMapSquareLocationMutation,
  useUnmapSquareLocationMutation,
  useTriggerSquareSyncMutation,
  useClearSquareCacheMutation,
} = neoSquareAuthApi
